import React, { useMemo, useState } from 'react';
import FilterDrawer from '../ui/FilterDrawer';
import { InterviewMode, InterviewService } from '../../lib/services/interviews';
import { GoogleMap, LoadScript, StandaloneSearchBox, Marker } from '@react-google-maps/api';

interface CreateSlotDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  defaultTimezone?: string;
  defaultRequirementId?: string;
  initialStartIso?: string;
  initialEndIso?: string;
  onCreated?: () => void;
}

export const CreateSlotDrawer: React.FC<CreateSlotDrawerProps> = ({ isOpen, onClose, defaultTimezone, defaultRequirementId, initialStartIso, initialEndIso, onCreated }) => {
  const [title, setTitle] = useState('Interview slot');
  const [startIso, setStartIso] = useState<string>(initialStartIso || '');
  const [endIso, setEndIso] = useState<string>(initialEndIso || '');
  const [mode, setMode] = useState<InterviewMode>('google_meet');
  const [joinLink, setJoinLink] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [locationLatLng, setLocationLatLng] = useState<{ lat: number; lng: number } | null>(null);
  const [capacity, setCapacity] = useState<number>(1);
  const mapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
  const center = useMemo(() => ({ lat: 12.9716, lng: 77.5946 }), []);
  const [searchBox, setSearchBox] = useState<google.maps.places.SearchBox | null>(null);
  const onPlacesChanged = () => {
    if (!searchBox) return;
    const places = searchBox.getPlaces();
    if (places && places.length > 0) {
      const place = places[0];
      const name = place.formatted_address || place.name || '';
      setLocation(name);
      const loc = place.geometry?.location;
      if (loc) setLocationLatLng({ lat: loc.lat(), lng: loc.lng() });
    }
  };
  const [clientId, setClientId] = useState<string>('');
  const [panelists, setPanelists] = useState<Array<{ name: string; email?: string }>>([{ name: '' }]);
  const [timezone, setTimezone] = useState<string>(defaultTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');

  React.useEffect(() => {
    if (initialStartIso) setStartIso(initialStartIso);
    if (initialEndIso) setEndIso(initialEndIso);
  }, [initialStartIso, initialEndIso]);

  const addPanelist = () => setPanelists(prev => [...prev, { name: '' }]);
  const updatePanelist = (idx: number, key: 'name' | 'email', value: string) => {
    setPanelists(prev => prev.map((p, i) => i === idx ? { ...p, [key]: value } : p));
  };
  const removePanelist = (idx: number) => setPanelists(prev => prev.filter((_, i) => i !== idx));

  const onApply = () => {
    if (!startIso || !endIso) return;
    InterviewService.create({
      title,
      startIso,
      endIso,
      mode,
      joinLink: mode === 'google_meet' ? (joinLink || 'https://meet.google.com/mock-link') : (mode === 'no_link' || mode === 'in_person' ? undefined : joinLink || undefined),
      location: mode === 'in_person' ? (location || '') : undefined,
      capacity,
      clientId: clientId || undefined,
      requirementId: defaultRequirementId || undefined,
      panelists,
      timezone,
    });
    onClose();
    onCreated && onCreated();
  };

  return (
    <FilterDrawer
      isOpen={isOpen}
      onClose={onClose}
      onApply={onApply}
      title="Create Interview Slot"
      widthClassName="w-[420px]"
    >
      <div className="space-y-3 text-sm">
        <div>
          <label className="block mb-1">Title</label>
          <input className="w-full border rounded px-2 h-9" value={title} onChange={e => setTitle(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block mb-1">Start</label>
            <input type="datetime-local" className="w-full border rounded px-2 h-9" value={startIso} onChange={e => setStartIso(e.target.value)} />
          </div>
          <div>
            <label className="block mb-1">End</label>
            <input type="datetime-local" className="w-full border rounded px-2 h-9" value={endIso} onChange={e => setEndIso(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="block mb-1">Timezone</label>
          <input className="w-full border rounded px-2 h-9" value={timezone} onChange={e => setTimezone(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block mb-1">Mode</label>
            <select className="w-full border rounded px-2 h-9" value={mode} onChange={e => setMode(e.target.value as InterviewMode)}>
              <option value="google_meet">Google Meet</option>
              <option value="ms_teams">MS Teams</option>
              <option value="zoom">Zoom</option>
              <option value="webex">Webex</option>
              <option value="other">Other</option>
              <option value="no_link">No Link</option>
              <option value="in_person">In-person (F2F)</option>
            </select>
          </div>
          <div>
            <label className="block mb-1">Join Link</label>
            <input className="w-full border rounded px-2 h-9" placeholder="Optional" value={joinLink} onChange={e => setJoinLink(e.target.value)} />
          </div>
        </div>
        {mode === 'in_person' && (
          <div className="space-y-2">
            <div>
              <label className="block mb-1">Location</label>
              {mapsApiKey ? (
                <LoadScript googleMapsApiKey={mapsApiKey} libraries={["places"]}>
                  <StandaloneSearchBox onLoad={ref => setSearchBox(ref)} onPlacesChanged={onPlacesChanged}>
                    <input className="w-full border rounded px-2 h-9" placeholder="Search address or place" defaultValue={location} />
                  </StandaloneSearchBox>
                  <div className="mt-2 h-48 w-full rounded overflow-hidden border">
                    <GoogleMap mapContainerStyle={{ width: '100%', height: '100%' }} zoom={12} center={locationLatLng || center}>
                      {locationLatLng && <Marker position={locationLatLng} />}
                    </GoogleMap>
                  </div>
                </LoadScript>
              ) : (
                <input className="w-full border rounded px-2 h-9" placeholder="Address or meeting location" value={location} onChange={e => setLocation(e.target.value)} />
              )}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block mb-1">Capacity</label>
            <input type="number" min={1} className="w-full border rounded px-2 h-9" value={capacity} onChange={e => setCapacity(Number(e.target.value || 1))} />
          </div>
          <div>
            <label className="block mb-1">Client (optional)</label>
            <input className="w-full border rounded px-2 h-9" value={clientId} onChange={e => setClientId(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="block mb-1">Panelists</label>
          <div className="space-y-2">
            {panelists.map((p, i) => (
              <div key={i} className="grid grid-cols-7 gap-2 items-center">
                <input className="col-span-3 border rounded px-2 h-9" placeholder="Name" value={p.name} onChange={e => updatePanelist(i, 'name', e.target.value)} />
                <input className="col-span-3 border rounded px-2 h-9" placeholder="Email (optional)" value={p.email || ''} onChange={e => updatePanelist(i, 'email', e.target.value)} />
                <button onClick={() => removePanelist(i)} className="text-xs px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50">Remove</button>
              </div>
            ))}
            <button onClick={addPanelist} className="text-xs px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50">Add Panelist</button>
          </div>
        </div>
      </div>
    </FilterDrawer>
  );
};

export default CreateSlotDrawer;


